import { isSidebarItemActive } from "src/utils";
import { SidebarActionTypes as ActionTypes } from "./action-types";
import { ComponentProps } from "react";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";

export enum VisageSidebarMenuItemActiveMode {
    exact = 1,
    relative = 2
}

export enum VisageSidebarDrawerId {
    none = 1,
    groups = 2,
    messages = 3,
}

export interface IVisageSidebarMenuItem {
    key: string,
    name: string,
    icon?: string,
    iconStyle?: ComponentProps<typeof Visage2Icon>['type'];
    iconRotation?: number;
    activeIcon?: string;
    route?: string,
    routes?: string[],
    search?: string,
    smallerIcon?: boolean,
    iconColor?: string,
    small?: boolean,
    children?: IVisageSidebarMenuItem[],
    isExpanded?: boolean;
    isActive?: boolean;
    unreadCount?: number;
    parentKey?: string;
    activeMode?: VisageSidebarMenuItemActiveMode;
    drawerId?: VisageSidebarDrawerId;
    moduleKey?: string;
    isLoading?: boolean;
    enableChildrenSearch?: boolean;
    dummyClassName?: string;
    imageUrl?: string;
    color?: string;
    menuClassName?: string;
    onClick?: (item: IVisageSidebarMenuItem) => boolean | undefined;
    lazyLoadChildren?: boolean;
    trailingIcon?: string;
    trailingIconTitle?: string;
    onTrailingIconClick?: (item: IVisageSidebarMenuItem) => void;
    isSectionLabel?: boolean;
    searchPath?: string;
}

/**
 * Turns a submenu into the domain's own menu (back row, heading, search, rows)
 * instead of merging it into the main menu.
 */
export interface IVisageSidebarDomainMenu {
    /** Heading used when the customer's main menu has no item with moduleKey. */
    title: string;
    /** The main menu item whose name is used as heading, as the customer calls it. */
    moduleKey?: string;
    searchPlaceholder: string;
    /** Route prefixes (whole path segments) that keep the domain menu open. */
    routes?: string[];
    /** Set by the reducer: true when today's behaviour merged the items into the main menu. */
    mergedIntoMainMenu?: boolean;
    /** Opening a top-level row closes the other top-level rows. */
    singleOpenBranch?: boolean;
    /** Shown when the search finds nothing, e.g. "Ingen sida heter så". */
    noHitsTitle?: string;
    /**
     * Searches the domain's content, not only its rows: the query is appended to this
     * address (e.g. "/news?searchText="), shown as a last row and opened on Enter.
     */
    contentSearchUrl?: string;
    /** A first row back to the domain's own list, e.g. "Alla nyheter" on /news. */
    rootRow?: {
        name: string;
        route: string;
    };
}

export enum VisageSidebarMode {
    menu = 1,
    submenu = 2,
    drawer = 3,
    scoped = 4,
    scopedContracted = 5,
    scopedDrawer = 6,
    noMenu = 7,
}

export interface ISidebarState {
    items: IVisageSidebarMenuItem[];
    mode: VisageSidebarMode;
    drawerId: VisageSidebarDrawerId;
    menuItems: IVisageSidebarMenuItem[];
    menuFetchHasBeenInitialized: boolean;
    displaySearch: boolean;
    displayBackButton: boolean;
    domainMenu?: IVisageSidebarDomainMenu;
}

const initialState: ISidebarState = {
    items: [],
    mode: VisageSidebarMode.menu,
    drawerId: VisageSidebarDrawerId.none,
    menuItems: [],
    menuFetchHasBeenInitialized: false,
    displaySearch: false,
    displayBackButton: true,
    domainMenu: undefined
};

const setItemChildren = (items: IVisageSidebarMenuItem[], children: IVisageSidebarMenuItem[], key: string) => {
    return items.map((item: IVisageSidebarMenuItem) => {
        if (item.key === key || item.moduleKey === key) {
            return {
                ...item,
                children,
                isExpanded: true
            }
        } else if (!!item.children && item.children.length > 0) {
            return {
                ...item,
                children: setItemChildren(item.children, children, key),
            }
        } else {
            return item;
        }
    })
}

const doesItemsContainMenuItem = (items: IVisageSidebarMenuItem[], key: string) => {
    let existsInMenu = false;

    for (const item of items) {
        if (existsInMenu) {
            continue;
        }

        const isMatch = (!!item.key && item.key === key) ||
            (!!item.moduleKey && item.moduleKey === key);

        if (isMatch) {
            existsInMenu = true;
        }

        if (item.children) {
            const existsInChildren = doesItemsContainMenuItem(item.children, key);

            if (existsInChildren) {
                existsInMenu = true;
            }
        }
    }

    return existsInMenu;
}

const setItemExpanded = (items: IVisageSidebarMenuItem[], key: string, value: boolean, isLoading?: boolean) => {
    return items.map((item: IVisageSidebarMenuItem, index: number) => {
        if (item.key === key) {
            return {
                ...item,
                isExpanded: value,
                isLoading: isLoading
            }
        } else if (!!item.children && item.children.length > 0) {
            return {
                ...item,
                children: setItemExpanded(item.children, key, value)
            }
        } else {
            return item;
        }
    })
}

// The children to merge into the main menu: the row with the key when several rows are given
const getMergeChildren = (items: IVisageSidebarMenuItem[], key: string): IVisageSidebarMenuItem[] => {
    if (items.length === 1) {
        return items[0].children;
    }

    const keyed = items.find((item) => item.key === key || item.moduleKey === key);

    return keyed ? keyed.children : items;
}

const sidebarReducer = (state: ISidebarState = initialState, action: any) => {
    switch (action.type) {
        case ActionTypes.SetMenu:
            return {
                ...state,
                menuItems: action.items,
                menuFetchHasBeenInitialized: true,
                items: state.mode === VisageSidebarMode.menu || state.mode === VisageSidebarMode.drawer ? action.items : state.items
            }
        case ActionTypes.SetSidebarItems:
            let existsInMenu = false;

            if (action.key) {
                existsInMenu = doesItemsContainMenuItem(state.menuItems, action.key);
            }

            if (action.domainMenu) {
                return {
                    ...state,
                    items: [...action.items],
                    // Keep the main menu as today, so "Gå tillbaka" shows the same menu as before
                    menuItems: existsInMenu ?
                        setItemChildren(state.menuItems, getMergeChildren(action.items, action.key), action.key) :
                        state.menuItems,
                    mode: VisageSidebarMode.submenu,
                    domainMenu: {
                        ...action.domainMenu,
                        mergedIntoMainMenu: existsInMenu
                    }
                };
            }

            if (existsInMenu) {
                const children = action.items.length === 1 ? action.items[0].children : action.items;
                const newMenuItems = setItemChildren(state.menuItems, children, action.key);

                return {
                    ...state,
                    items: newMenuItems,
                    menuItems: newMenuItems,
                    mode: VisageSidebarMode.menu,
                    domainMenu: undefined
                }
            } else {
                return {
                    ...state,
                    items: [...action.items],
                    mode: VisageSidebarMode.submenu,
                    domainMenu: undefined
                };
            }
        case ActionTypes.SetItemChildren:
            return {
                ...state,
                items: setItemChildren(state.items, action.items, action.key)
            }
        case ActionTypes.SetItemExpanded:
            const existsInMenu2 = doesItemsContainMenuItem(state.menuItems, action.key);
            const closeOtherBranches = action.value &&
                !!state.domainMenu &&
                !!state.domainMenu.singleOpenBranch &&
                state.items.some((item: IVisageSidebarMenuItem) => item.key === action.key);
            const items = closeOtherBranches ?
                state.items.map((item: IVisageSidebarMenuItem) => item.key !== action.key && item.isExpanded ?
                    { ...item, isExpanded: false } :
                    item) :
                state.items;

            return {
                ...state,
                items: setItemExpanded(items, action.key, action.value, action.isLoading),
                menuItems: existsInMenu2 ?
                    setItemExpanded(state.menuItems, action.key, action.value, false) :
                    state.menuItems
            }
        case ActionTypes.ExpandToActiveItem:
            const activeKeys: string[] = [];
            const flatList: IVisageSidebarMenuItem[] = [];

            const addItemsToFlatList = (items: IVisageSidebarMenuItem[], parentKey: string) => {
                for (const item of items) {
                    flatList.push({
                        ...item,
                        parentKey
                    });

                    if (item.children && item.children.length > 0) {
                        addItemsToFlatList(item.children, item.key);
                    }
                }
            }

            addItemsToFlatList(state.items, "");

            const addItemToActiveKeys = (item: IVisageSidebarMenuItem) => {
                activeKeys.push(item.key);

                if (item.parentKey) {
                    const parent = flatList.find((i: any) => i.key === item.parentKey);

                    if (parent) {
                        addItemToActiveKeys(parent)
                    }
                }
            }

            for (const item of flatList) {
                if (isSidebarItemActive(item)) {
                    addItemToActiveKeys(item);
                }
            }

            const getCollection = (items: IVisageSidebarMenuItem[]) => {
                return items.map((item: IVisageSidebarMenuItem, index: number) => {
                    return {
                        ...item,
                        isExpanded: item.isExpanded ? true : activeKeys.indexOf(item.key) > -1,
                        children: item.children ? getCollection(item.children) : item.children
                    }
                })
            }

            return {
                ...state,
                items: getCollection(state.items)
            }
        case ActionTypes.SetSidebarMode:
            if (action.mode === VisageSidebarMode.submenu) {
                return state;
            }

            return {
                ...state,
                mode: action.mode,
                items: action.mode === VisageSidebarMode.menu ? state.menuItems : state.items,
                domainMenu: undefined
            }
        case ActionTypes.SetSidebarDrawerId:
            return {
                ...state,
                drawerId: action.value
            }
        case ActionTypes.SetDisplaySearch:
            return {
                ...state,
                displaySearch: action.value
            }
        case ActionTypes.SetDisplayBackButton:
            return {
                ...state,
                displayBackButton: action.value
            }
        default:
            return state;
    }
}

export default sidebarReducer;
